import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';

export type NoticeTone = 'danger' | 'warning' | 'info' | 'success' | 'neutral';

/**
 * The one inline banner: a surface box with a coloured left edge, a small
 * icon, an optional bold title and the body text. It replaces the hand-rolled
 * tinted banner divs and the local `Notice` / `ErrorBlock` copies.
 *
 * - **No `role` unless the caller passes one.** A notice that renders on load
 *   is not an alert; one that appears in answer to an action may be. The call
 *   site knows which, this component does not.
 * - **Tone is never the only signal**: the icon differs per tone and the
 *   sentence carries the meaning. Wording is always the caller's.
 * - The body is a `div`, not a `p`, because callers pass block content (a
 *   sentence plus a link or a list), and a block inside a paragraph is invalid
 *   markup that throws a console error.
 */
export interface NoticeProps {
  tone: NoticeTone;
  title?: ReactNode;
  role?: 'alert' | 'status';
  className?: string;
  children?: ReactNode;
  /** A control beside the text — a retry, a link. */
  action?: ReactNode;
}

const EDGE: Record<NoticeTone, string> = {
  danger: 'border-l-danger',
  warning: 'border-l-warning',
  info: 'border-l-info',
  success: 'border-l-success',
  neutral: 'border-l-hairline-strong',
};

const ICON_TONE: Record<NoticeTone, string> = {
  danger: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
  success: 'text-success',
  neutral: 'text-fg-subtle',
};

function NoticeIcon({ tone }: { tone: NoticeTone }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: 'h-4 w-4',
    'aria-hidden': true,
  };
  if (tone === 'success') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12 2.5 2.5 4.5-5" />
      </svg>
    );
  }
  if (tone === 'danger' || tone === 'warning') {
    return (
      <svg {...common}>
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  );
}

export function Notice({ tone, title, role, className, children, action }: NoticeProps) {
  return (
    <div
      role={role}
      className={cx(
        'flex gap-2.5 rounded-md border border-l-[3px] border-border bg-surface px-3 py-2 text-xs text-fg-secondary',
        EDGE[tone],
        className,
      )}
    >
      <span className={cx('mt-px shrink-0', ICON_TONE[tone])}>
        <NoticeIcon tone={tone} />
      </span>
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold text-fg">{title}</div>}
        {children && <div className={title ? 'mt-0.5' : undefined}>{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
